import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import axios from "axios";
import {
  type AdminBidDto,
  AdminProductCategoriesApi,
  AdminProductsApi,
  type AdminProductSkuDto,
  type CreateProductListingRequest,
  type EventStatus,
  PublicInventoryLocationType,
  type SalesMethod,
  type SkuVisibility,
  type SortDirection,
  type SortProductListingOn,
  type UpdateProductCategoriesRequest,
  type UpdateProductPriceRequest,
  type UpdateProductSkuRequestDto,
} from "mws-catalog-ts-sdk";
import {
  type AddCategoriesToProductListingsRequest,
  type AdminListing,
  AdminProductListingsApi,
  AdminProductsApi as AdminProductsApiV2,
  type AdminProductsApiV2AdminProductsProductIdMediaSwitchPostRequest,
  ImageOrigin,
  type MoveProductListingRequest,
  type UpdateProductListingRequest,
} from "mws-catalog-ts-sdk-v2-admin";
import type { Ref } from "vue";
import { useToast } from "vue-toastification";

import type { Guid, UseQueryOptionsOmitQueryKey } from "@/common/types";
import { formKitDateNoTime } from "@/common/utils/dateUtils";
import type { ProductSku } from "@/products/types";

/**
 * Maps SDK enum values to backend-expected values.
 * The SDK generates snake case enum values (_short, _long) due to SnakeCaseSchemaFilter,
 * but the backend expects the original enum names (short, long).
 */
const mapStorageLocationTypesToBackendFormat = (
  storageLocationTypes?: PublicInventoryLocationType[],
): PublicInventoryLocationType[] | undefined => {
  if (!storageLocationTypes) return undefined;

  return storageLocationTypes.map((type) => {
    // Convert snake case enum values to backend-expected format
    switch (type) {
      case PublicInventoryLocationType.Short: // "_short"
        return "short" as PublicInventoryLocationType;
      case PublicInventoryLocationType.Long: // "_long"
        return "long" as PublicInventoryLocationType;
      default:
        return type;
    }
  });
};

const catalogApiUrl = import.meta.env.VITE_CATALOG_API;
const adminProductsApi = new AdminProductsApi(undefined, catalogApiUrl);
const adminProductsApiV2 = new AdminProductsApiV2(undefined, catalogApiUrl);
const adminProductListingApi = new AdminProductListingsApi(
  undefined,
  catalogApiUrl,
);
const adminProductCategoriesApi = new AdminProductCategoriesApi(
  undefined,
  catalogApiUrl,
);

export const useGetProductListingsListQuery = (
  searchTerm: Ref<string>,
  pageSize: Ref<number>,
  offset: Ref<number>,
  filters: Ref<{
    platformKey: string;
    sortOn?: SortProductListingOn;
    sortDirection?: SortDirection;
    salesMethodFilter?: SalesMethod;
    productId?: string;
    eventIds?: string[];
    eventStatuses?: EventStatus[];
    visibility?: SkuVisibility;
    endDate?: Date;
    categoryIds?: string[];
    platformIds?: string[];
    priceRange?: { lower?: number; upper?: number };
    storageLocationTypes?: PublicInventoryLocationType[];
    productTypeIds?: string[];
    shippingOfficeIds?: string[];
    hasBids?: boolean;
    properties?: string[];
  }>,
) => {
  const queryKey = ["products", { searchTerm, pageSize, offset, filters }];

  const queryFn = async () => {
    // Convert properties filter to boolean flags
    const hasBids = filters?.value.properties?.includes("hasBids")
      ? true
      : undefined;

    return (
      await adminProductsApi.adminProductsGet({
        searchTerm: searchTerm.value || undefined,
        xPlatformKey: filters?.value.platformKey,
        offset: offset.value,
        pageSize: pageSize.value,
        sortProductListingOn: filters?.value.sortOn,
        sortDirection: filters?.value.sortDirection,
        salesMethodFilter: filters?.value.salesMethodFilter,
        productId: filters?.value.productId,
        eventIds: filters?.value.eventIds,
        eventStatuses: filters?.value.eventStatuses,
        visibility: filters?.value.visibility,
        endDate: formKitDateNoTime(filters?.value.endDate),
        categoryIds: filters?.value.categoryIds,
        priceRangeUpper: filters?.value.priceRange?.upper,
        priceRangeLower: filters?.value.priceRange?.lower,
        storageLocationTypes: mapStorageLocationTypesToBackendFormat(
          filters?.value.storageLocationTypes,
        ),
        productTypeIds: filters?.value.productTypeIds,
        shippingOfficeIds: filters?.value.shippingOfficeIds,
        hasBids: hasBids,
      })
    ).data;
  };

  const options = { keepPreviousData: true };

  return useQuery({ queryKey, queryFn, ...options });
};

export const useGetProductListingByIdQuery = (
  productListingId: Ref<Guid | undefined>,
  options?: UseQueryOptionsOmitQueryKey<AdminProductSkuDto>,
) => {
  const queryKey = ["products", productListingId];

  const queryFn = async () => {
    return (
      await adminProductsApi.adminProductsSkuIdGet({
        skuId: productListingId.value || "",
      })
    ).data;
  };

  return useQuery({ queryKey, queryFn, ...options });
};

export const useGetProductListingByIdV2Query = (
  productListingId: Ref<Guid | undefined>,
  options?: UseQueryOptionsOmitQueryKey<AdminListing>,
) => {
  const queryKey = ["products", productListingId, "V2"];

  const queryFn = async () => {
    return (
      await adminProductListingApi.v2AdminProductListingsIdGet({
        id: productListingId.value || "",
      })
    ).data;
  };

  return useQuery({ queryKey, queryFn, ...options });
};

export const usePatchProductListingDetailsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      id: string;
      updateProductListingRequest: UpdateProductListingRequest;
    }) => {
      const response =
        await adminProductListingApi.v2AdminProductListingsIdPatch(request);

      return response.data;
    },

    onSuccess: (_, request) => {
      queryClient.invalidateQueries({ queryKey: ["products", request.id] });
    },
  });
};

export const usePatchProductListingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      skuId: Guid;
      updateProductSkuRequestDto: UpdateProductSkuRequestDto;
    }) => {
      const response = await adminProductsApi.adminProductsSkuIdPatch(request);

      return response.data;
    },

    onSuccess: (data, request) => {
      queryClient.invalidateQueries({ queryKey: ["products", request.skuId] });
      queryClient.invalidateQueries({
        queryKey: ["products", request.skuId, "V2"],
      });

      queryClient.invalidateQueries({
        queryKey: ["products", data.productId],
      });

      queryClient.invalidateQueries({
        queryKey: ["events"],
      });
    },
  });
};

export const usePatchProductListingPriceMutation = () => {
  const toast = useToast();

  return useMutation({
    mutationFn: async (request: {
      skuId: Guid;
      updateProductPriceRequest: UpdateProductPriceRequest;
    }) => {
      const response = await adminProductsApi.adminProductsSkuIdPricesPatch({
        skuId: request.skuId,
        updateProductPriceRequest: request.updateProductPriceRequest,
      });

      return response.data;
    },

    onSuccess: () => {
      toast.success("Prices updated successfully!");
    },
  });
};

export const useCreateProductListingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateProductListingRequest) => {
      const response = await adminProductsApi.adminProductsPost({
        createProductListingRequest: request,
      });

      return response.data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
};

export const useUpdateProductListingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (productListing: Partial<ProductSku>) => {
      const response = await axios.patch(
        `${catalogApiUrl}/admin/products/${productListing.id}/`,
        productListing,
      );

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userOrders"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
};

export const useGetProductListingBidsQuery = (
  productId: Ref<Guid>,
  options?: UseQueryOptionsOmitQueryKey<AdminBidDto[]>,
) => {
  const queryKey = ["products", productId, "bids"];

  const queryFn = async () => {
    return (
      await adminProductsApi.adminProductsSkuIdBidsGet({
        skuId: productId.value,
      })
    ).data;
  };

  return useQuery({ queryKey, queryFn, ...options });
};

export const useUpdateProductListingCategoriesMutation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (request: UpdateProductCategoriesRequest) => {
      const response =
        await adminProductCategoriesApi.adminProductCategoriesPost({
          updateProductCategoriesRequest: request,
        });

      return response.data;
    },
    onSuccess: async (productListing: AdminProductSkuDto) => {
      await queryClient.invalidateQueries({
        queryKey: ["products", productListing.id],
      });
      toast.success("Product Listing Categories Updated");
    },
  });
};

export const useAddCategoriesToProductListingsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: AddCategoriesToProductListingsRequest) => {
      await adminProductListingApi.v2AdminProductListingsCategoriesPost({
        addCategoriesToProductListingsRequest: request,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["products"] });
      await queryClient.invalidateQueries({
        queryKey: ["events", "products", "list"],
      });
    },
  });
};

export const useSwitchProductListingMediaMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      request: AdminProductsApiV2AdminProductsProductIdMediaSwitchPostRequest,
    ) => {
      const response =
        await adminProductsApiV2.v2AdminProductsProductIdMediaSwitchPost({
          productId: request.productId,
          switchProductMediaRequest: {
            imageOriginToSwitchTo:
              request.switchProductMediaRequest?.imageOriginToSwitchTo ??
              ImageOrigin.Photo,
          },
        });

      return response.data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: ["products", vars.productId],
      });
      queryClient.invalidateQueries({
        queryKey: ["events", "products", "list"],
      });
    },
  });
};

export const useDeleteProductListingMutation = (
  onSuccess?: (deletedProductListingId: string) => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await adminProductListingApi.v2AdminProductListingsIdDelete({ id });

      return id;
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["products", variables.id],
      });
      await queryClient.invalidateQueries({ queryKey: ["products"] });
      await queryClient.invalidateQueries({
        queryKey: ["events", "products", "list"],
      });
      onSuccess?.(variables.id);
    },
  });
};

export const useMoveProductListingMutation = (
  onSuccess?: (movedProductListingId: string) => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      id: string;
      moveProductListingRequest: MoveProductListingRequest;
    }) => {
      await axios.post(
        `${catalogApiUrl}/v2/admin/product-listings/${request.id}/move`,
        request.moveProductListingRequest,
      );

      return request.id;
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["products", variables.id],
      });
      await queryClient.invalidateQueries({ queryKey: ["products"] });
      await queryClient.invalidateQueries({
        queryKey: ["events", "products", "list"],
      });
      onSuccess?.(variables.id);
    },
  });
};
